import React, { useState, useEffect } from 'react';
import { 
  Search, Eye, Download, Users, Briefcase, GraduationCap, 
  MapPin, Clock, Calendar, X, Building2, Globe2 
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Enquiry Interface matching your Spring Boot Entity
interface Enquiry {
  id: number;
  name: string;
  email: string;
  mobile: string;
  collegeName: string;
  isWorking: string;
  companyName: string;
  experience: string;
  category: string;
  course: string;
  source: string;
  otherSource: string;
  createdAt: string; 
}

const AdminAdEnquiries: React.FC = () => {
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEnquiry, setSelectedEnquiry] = useState<Enquiry | null>(null);

  // Fetch data from Spring Boot
  useEffect(() => {
    fetchEnquiries();
  }, []);

  const fetchEnquiries = async () => {
    try {
      const response = await fetch('https://www.projectinfotech.in/api/enquiry/all');
      if (response.ok) {
        const data = await response.json();
        // Reverse to show newest first (or handle sorting in backend)
        setEnquiries(data.reverse()); 
      } else {
        console.error("Failed to fetch enquiries");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Search Filter
  const filteredEnquiries = enquiries.filter(enq => 
    enq.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    enq.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    enq.mobile.includes(searchTerm)
  );

  // Export to CSV Function
  const exportToCSV = () => {
    const headers = ["ID,Name,Email,Mobile,Status,Company,Experience,Category,Course,Source,Date\n"];
    const csvData = filteredEnquiries.map(e => 
      `${e.id},"${e.name}","${e.email}","${e.mobile}","${e.isWorking}","${e.companyName || '-'}","${e.experience || '-'}","${e.category}","${e.course}","${e.source}","${new Date(e.createdAt).toLocaleDateString()}"`
    ).join("\n");
    
    const blob = new Blob([headers + csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PIT_Ad_Enquiries_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-gray-50/50 p-6 md:p-10 font-sans">
      <div className="max-w-7xl mx-auto">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-black text-gray-900 tracking-tight">Ad Campaign Leads</h1>
            <p className="text-gray-500 font-medium mt-1 flex items-center gap-2">
              <Users size={16} className="text-red-600"/> 
              Total {enquiries.length} Enquiries Received
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="relative group">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 group-focus-within:text-red-600 transition-colors" />
              <input 
                type="text" 
                placeholder="Search name, email, phone..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:border-red-600 focus:ring-4 focus:ring-red-600/10 outline-none w-full md:w-72 transition-all"
              />
            </div>
            <button 
              onClick={exportToCSV}
              className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-4 py-2.5 rounded-xl hover:bg-gray-50 hover:text-gray-900 transition-colors font-semibold shadow-sm"
            >
              <Download size={18} /> Export
            </button>
          </div>
        </div>

        {/* Data Table Card */}
        <div className="bg-white rounded-[1.5rem] shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/80 border-b border-gray-100">
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Applicant Info</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Interested In</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-20 text-gray-400 font-medium">
                      Loading latest enquiries...
                    </td>
                  </tr>
                ) : filteredEnquiries.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-20 text-gray-400 font-medium">
                      No enquiries found matching your search.
                    </td>
                  </tr>
                ) : (
                  filteredEnquiries.map((enq) => (
                    <tr key={enq.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center font-bold text-sm">
                            {enq.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-bold text-gray-900">{enq.name}</p>
                            <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                              <Calendar size={12}/> {enq.createdAt ? new Date(enq.createdAt).toLocaleDateString() : 'N/A'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-900 font-medium">{enq.mobile}</p>
                        <p className="text-xs text-gray-500">{enq.email}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${
                          enq.isWorking === 'Yes' ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'bg-green-50 text-green-700 border border-green-100'
                        }`}>
                          {enq.isWorking === 'Yes' ? <Briefcase size={12}/> : <GraduationCap size={12}/>}
                          {enq.isWorking === 'Yes' ? 'Working' : 'Student'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-bold text-gray-900 line-clamp-1">{enq.course}</p>
                        <p className="text-xs text-gray-500">{enq.category}</p>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button 
                          onClick={() => setSelectedEnquiry(enq)}
                          className="inline-flex items-center justify-center p-2 bg-gray-50 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-gray-200 hover:border-red-200"
                          title="View Details"
                        >
                          <Eye size={18} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* --- View Details Modal --- */}
      <AnimatePresence>
        {selectedEnquiry && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setSelectedEnquiry(null)}
              className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm"
            />
            
            {/* Modal Content */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-white rounded-[2rem] shadow-2xl w-full max-w-2xl overflow-hidden"
            >
              {/* Modal Header */}
              <div className="bg-gray-50 px-8 py-6 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-black text-gray-900">Lead Details</h3>
                  <p className="text-sm text-gray-500 mt-1">ID: #{selectedEnquiry.id} • Submitted on {selectedEnquiry.createdAt ? new Date(selectedEnquiry.createdAt).toLocaleString() : 'N/A'}</p>
                </div>
                <button 
                  onClick={() => setSelectedEnquiry(null)}
                  className="p-2 bg-white text-gray-400 hover:text-red-600 rounded-full shadow-sm border border-gray-100 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-8 space-y-8 max-h-[70vh] overflow-y-auto">
                
                {/* Section 1: Personal */}
                <div>
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2">Personal Details</h4>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Full Name</p>
                      <p className="font-bold text-gray-900">{selectedEnquiry.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Email Address</p>
                      <p className="font-bold text-gray-900">{selectedEnquiry.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Mobile Number</p>
                      <p className="font-bold text-gray-900">{selectedEnquiry.mobile}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">College / University</p>
                      <p className="font-bold text-gray-900 flex items-center gap-1.5"><GraduationCap size={16} className="text-gray-400"/> {selectedEnquiry.collegeName}</p>
                    </div>
                  </div>
                </div>

                {/* Section 2: Professional */}
                <div>
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2">Professional Status</h4>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Current Status</p>
                      <span className={`inline-flex px-2.5 py-1 rounded text-xs font-bold ${selectedEnquiry.isWorking === 'Yes' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>
                        {selectedEnquiry.isWorking === 'Yes' ? 'Working Professional' : 'Student / Fresher'}
                      </span>
                    </div>
                    {selectedEnquiry.isWorking === 'Yes' && (
                      <>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Company Name</p>
                          <p className="font-bold text-gray-900 flex items-center gap-1.5"><Building2 size={16} className="text-gray-400"/> {selectedEnquiry.companyName}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Total Experience</p>
                          <p className="font-bold text-gray-900 flex items-center gap-1.5"><Clock size={16} className="text-gray-400"/> {selectedEnquiry.experience} Years</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Section 3: Program & Source */}
                <div>
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2">Program & Tracking</h4>
                  <div className="grid grid-cols-2 gap-6 bg-gray-50 p-4 rounded-xl border border-gray-100">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Selected Course</p>
                      <p className="font-bold text-red-600">{selectedEnquiry.course}</p>
                      <p className="text-xs text-gray-500 mt-0.5">Category: {selectedEnquiry.category}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Source / Medium</p>
                      <p className="font-bold text-gray-900 flex items-center gap-1.5"><Globe2 size={16} className="text-gray-400"/> {selectedEnquiry.source}</p>
                      {selectedEnquiry.source === 'Other' && (
                        <p className="text-xs text-gray-500 mt-0.5">Details: {selectedEnquiry.otherSource}</p>
                      )}
                    </div>
                  </div>
                </div>

              </div>
              
              {/* Modal Footer */}
              <div className="bg-gray-50 px-8 py-4 border-t border-gray-100 flex justify-end">
                <a 
                  href={`https://wa.me/${selectedEnquiry.mobile.replace(/\D/g, '')}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-green-500 text-white px-6 py-2 rounded-lg font-bold hover:bg-green-600 transition-colors shadow-sm"
                >
                  Message on WhatsApp
                </a>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default AdminAdEnquiries;